app.service('CurrentPatient', ['Session','AppointmentTimerService','PatientAuditAPI', function(Session,AppointmentTimerService,PatientAuditAPI) {

	// A verify date belongs to the address or number that was verified, not to whatever is sitting
	// in the field now. The server clears the date when a changed value is saved, but between the
	// edit and the save the old date is still attached to a value nobody has verified - which would
	// otherwise show a freshly typed address as "Verified". patientBackup holds the patient as the
	// server last returned them, so it is what the current value has to match.
	function sameContactValue(current, saved) {
		return (current || "") === (saved || "");
	}

	// True when the value has changed since it was verified, which is what separates "never
	// verified" from "was verified, but not this one".
	function isEditedSinceVerified(verifyDate, current, verified) {
		return !!verifyDate && !sameContactValue(current, verified);
	}

	// The verify dates come back as TIMESTAMP WITHOUT TIME ZONE strings holding UTC, so they have
	// to be read as UTC. Parsing them as browser-local would be wrong by the offset.
	function formatVerifiedDate(_date) {
		if (!_date) return "";
		return moment.utc(_date).local().format("DD/MM/YYYY HH:mm");
	}

	var service = {

		patient: {},
		patientBackup: {},
		patientIsMine: false,
		eligibilitySummaryFirstLoad: true,
		isPatientNew: false,
		isPresetNewPatient: false,
		query: {},
	    patients: [],
		gp: null,

		setPatient: function (patient, appointment) {
			if (!patient) {
				return;
			}
			if(service.patient && service.patient.id !== patient.id){
				AppointmentTimerService.stopTimer();
			}
			service.isPatientNew = false;
			service.isPresetNewPatient = false;

			if(patient.id !== service.patient.id && patient.practice?.id === Session.practice.id){
				PatientAuditAPI.startAudit(patient.id);
			} else if (patient.id !== service.patient.id && service.patient.id) {
				// Switching to a patient we don't audit - still close the previous patient's audit.
				PatientAuditAPI.endAudit(service.patient.id);
			}

			// A save response carries the patient's true registered practice, which another
			// practice may have transferred away while this record was open. Don't lock the user
			// out of a patient they are already working on - keep treating it as theirs for the
			// rest of the session. Opening a different patient re-evaluates ownership normally.
			var keepsExistingOwnership = service.patientIsMine && !!patient.id && service.patient.id === patient.id;

			service.patient = patient;
			service.patient.appointment = appointment;
			service.patientBackup = angular.copy(patient);
			if (keepsExistingOwnership || service.patient.practice?.id === Session.practice.id) {
				service.patientIsMine = true;
			} else {
				service.patientIsMine = false;
			}
		
			
		},

		clearPatient: function() {
			service.isPatientNew = false;
			service.isPresetNewPatient = false;
			if(service.patient && service.patient.id){
				PatientAuditAPI.endAudit(service.patient.id);
			}
			service.patient = {};
			// Cleared with the patient. patientBackup is what "has this detail changed?" is measured
			// against - the save button's label, the verified pills - so leaving the last patient's
			// contacts in it makes the next patient's details read as edits of theirs.
			service.patientBackup = {};
			service.eligibilitySummaryFirstLoad = true;
			service.gp = null;
			
		},

		createNewPatient: function() {
			service.isPatientNew = true;
		},

		createNewPatientWithPreset: function() {
			service.isPatientNew = true;
			service.isPresetNewPatient = true;
		},

		setPatientResult: function(patients, form) {
			service.patients = patients;
			service.query = form;
		},
	
		clearPatientResult: function() {
			service.patients = [];
			service.query = {};
			service.isPatientNew = false;
			service.isPresetNewPatient = false;
			if(service.patient && service.patient.id){
				PatientAuditAPI.endAudit(service.patient.id);
			}
			service.patient = {};
			// See clearPatient - a stale backup makes the next patient's contacts read as edits.
			service.patientBackup = {};
			service.eligibilitySummaryFirstLoad = true;
			service.gp = null;
		},

		setGP: function (patientId, gp) {
			if (gp.length > 0 && service.patient && service.patient.id === patientId) {
				service.gp = angular.copy(gp[0]);
			}
		},

		getGP: function (patientId) {
			if (service.patient && service.patient.id === patientId) {
				return service.gp;
			}
			return null;
		},

		// Read on every call rather than cached, so switching practice takes effect straight away.
		isContactVerificationEnabled: function () {
			var features = Session.practice && Session.practice.practiceFeatures;
			return !!features && features.patientContactVerificationFeature === true;
		},

		// Contact verification state lives here rather than in a controller because more than one
		// screen asks the same question of the same objects: the pills on the patient edit form and
		// the comms icons in the patient header. Both read the live `patient`, so both need
		// `patientBackup` to tell a verified value from one that has been typed over since - and if
		// they answered it separately they could disagree on the same record.
		//
		// Each takes an optional patient and its saved copy, for a form editing a patient other than
		// the open one (an imported patient's review); without them it answers for the open patient.
		isEmailVerified: function (patient, saved) {
			patient = patient || service.patient;
			saved = saved || (patient === service.patient ? service.patientBackup : patient) || {};
			return !!patient.email_verify_date
				&& sameContactValue(patient.email, saved.email);
		},

		isMobileVerified: function (patient, saved) {
			patient = patient || service.patient;
			saved = saved || (patient === service.patient ? service.patientBackup : patient) || {};
			return !!patient.mobile_number_verify_date
				&& sameContactValue(patient.mobileNumber, saved.mobileNumber);
		},

		// These say what the state MEANS, not just what it is. "Unverified" on its own reads like a
		// fault in the record; what it actually means is that the patient has not followed the link
		// yet, which is ordinary and often just a matter of time.
		emailVerifiedTitle: function (patient, saved) {
			patient = patient || service.patient;
			saved = saved || (patient === service.patient ? service.patientBackup : patient) || {};
			if (service.isEmailVerified(patient, saved)) {
				return "Confirmed " + formatVerifiedDate(patient.email_verify_date)
					+ " via the link we emailed.";
			}
			if (isEditedSinceVerified(patient.email_verify_date, patient.email, saved.email)) {
				return "Address changed since it was confirmed. Saving emails a new link.";
			}
			return "Not yet confirmed - the patient has not followed the link we emailed.";
		},

		mobileVerifiedTitle: function (patient, saved) {
			patient = patient || service.patient;
			saved = saved || (patient === service.patient ? service.patientBackup : patient) || {};
			if (service.isMobileVerified(patient, saved)) {
				return "Confirmed " + formatVerifiedDate(patient.mobile_number_verify_date)
					+ " from the mobile itself.";
			}
			if (isEditedSinceVerified(patient.mobile_number_verify_date, patient.mobileNumber, saved.mobileNumber)) {
				return "Number changed since it was confirmed. Saving texts a new code.";
			}
			return "Not yet confirmed - the patient has not entered the code we texted.";
		}


	};

	return service;
}])